import styled from '@emotion/styled';
import { absoluteFill, mediaQuery } from '@presentation/styles';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import type { CardItemBadge } from '.';

export const CardItemContainer = styled.div`
  padding: 15px 0px;
  margin: -15px;
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
`;

export const CardItemWrapper = styled.div<{ skipFullWidthPhone?: boolean }>`
  min-height: 324px;
  padding: 15px;
  width: 25%;

  ${mediaQuery('desktop')} {
    width: 33.3%;
  }

  ${mediaQuery('tablet')} {
    width: 50%;
  }

  ${mediaQuery('phone')} {
    ${(props) => !props.skipFullWidthPhone && 'width: 100%;'}
  }
`;

export const Card = styled.div<{ backgroundColor?: string; hideHoverOverlay: boolean }>`
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.3);
  background: ${(props) => props.backgroundColor ?? props.theme.color.light.default};
  color: ${(props) => props.theme.color.dark.default};
  border-radius: 10px;
  position: relative;
  cursor: ${(props) => (props.hideHoverOverlay ? 'inherit' : 'pointer')};
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  text-align: left;
`;

export const Background = styled.div<{ shrinkHeightMobile?: boolean }>`
  height: 200px;
  position: relative;
  overflow: hidden;

  ${mediaQuery('phone')} {
    ${(props) => props.shrinkHeightMobile && 'height: 150px'}
  }
`;

export const LazyBackgroundImg = styled(LazyLoadImage)`
  height: 100%;
  width: 100%;
  object-fit: cover;
  position: relative;
  overflow: hidden;
`;

export const BackgroundImg = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
  position: relative;
  overflow: hidden;
`;

export const CardLink = styled.div`
  ${absoluteFill}
  z-index: 1;
  cursor: pointer;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

export const Overlay = styled.div`
  bottom: 0;
  height: 100%;
  width: 100%;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  padding: 20px;

  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  font-weight: 700;
  font-size: 18px;

  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

export const ImageButton = styled.div`
  position: absolute;
  color: pointer;
  right: 15px;
  top: 15px;
  z-index: 2;
`;

export const Content = styled.div<{ hasBadge: boolean }>`
  position: relative;
  padding: 20px;
  padding-bottom: 10px;
  ${(props) => props.hasBadge && 'padding-top: 30px;'}
`;

export const BadgeContainer = styled.div`
  left: 10px;
  top: -18px;
  position: absolute;
  z-index: 1;
  display: flex;
`;

export const BadgeWrapper = styled.div`
  margin-left: 10px;
  display: inline-block;
  background: white;
  padding: 5px;
  border-radius: 36px;
`;

export const SubtitleBadgeWrapper = styled.div`
  margin-top: 10px;
  display: flex;
`;

export const SubtitleBadge = styled.div<{ last?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  color: ${(props) => props.theme.color.dark.default};
  background: ${(props) => props.theme.color.light.shadeLighter};
  font-size: 14px;
  font-weight: 400;

  padding: 6px 14px;
  margin-right: ${(props) => (props.last ? '0px' : '14px')};

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Badge = styled.div<{
  label: string;
  color: CardItemBadge['color'];
  expanded?: boolean;
}>`
  padding: 10px;
  border-radius: 18px;
  color: ${(props) =>
    props.color === 'lightBlue' ? props.theme.color.blue.default : props.theme.color.light.default};
  background: ${(props) =>
    props.color === 'lightBlue'
      ? props.theme.color.blue.lightBlue
      : props.theme.color[props.color].default};
  display: flex;
  align-items: center;
  justify-content: center;
  &::after {
    max-width: 0;
    transition: all 0.3s ease-in-out;
    font-size: 11px;
    height: 16px;
    overflow: hidden;
    ${(props) => props.color === 'lightBlue' && `color: ${props.theme.color.blue.default};`}
    content: '${(props) => props.label}';
  }
  cursor: pointer;

  ${(props) =>
    props.expanded &&
    `
  &::after {
    display: inline-block;
    transition: all 0.3s ease-in-out;
    margin-left: 11px;
    max-width: 200px;
  }
  `}

  &:hover {
    &::after {
      display: inline-block;
      transition: all 0.3s ease-in-out;
      margin-left: 11px;
      max-width: 200px;
    }
  }
  img,
  span {
    width: 18px;
    height: 18px;
  }

  span {
    font-size: 14px;
    text-align: center;
    line-height: 18px;
  }
`;

export const LabelContainer = styled.div`
  right: 10px;
  top: -14px;
  position: absolute;
  background: white;
  border-radius: 4px;
  padding: 4px;
`;

export const ContentText = styled.div`
  font-size: 16px;
  font-weight: 300;
  margin-top: 5px;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  display: -webkit-box;
  display: -moz-box;
  line-height: 22px;
  max-height: 66px;
  overflow: hidden; */
`;

export const ContentTitle = styled.h2<{ dontTruncate?: boolean }>`
  margin: 0;
  font-size: 16px;
  font-weight: 600;

  ${(props) =>
    !props.dontTruncate &&
    `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  `}
`;

export const ContentTitleDiv = styled.div`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ContentSubtitle = styled.div`
  font-size: 16px;
  margin-top: 5px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const ContentHeaderSubtitle = styled.h2`
  font-size: 16px;
  margin-top: 5px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Footer = styled.div`
  margin-top: auto;
  border-top: 1px solid ${(props) => props.theme.color.medium.tint};
  padding: 10px 15px;
  font-size: 14px;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${(props) => props.theme.color.medium.default};
`;
